import * as React from "react"
import {
  type CategoryType,
  type EntityCategory,
  type EntityParty,
} from "@digits-graphql/frontend/graphql-bearer"
import { defined } from "@digits-shared/helpers/filters"
import { type DisplayOptionAccessor } from "src/frontend/components/OS/Shared/DisplayOptions/DisplayOption"
import { FilterByAmount } from "src/shared/components/Filters/FilterByAmount"
import { FilterByCategory } from "src/shared/components/Filters/FilterByCategory"
import { FilterByParty } from "src/shared/components/Filters/FilterByParty"

/*
 INTERFACES
*/

interface FiltersProps {
  categoryTypes: CategoryType[] | undefined | null
  categoryId: DisplayOptionAccessor<string>
  partyId: DisplayOptionAccessor<string>
  missingParty: DisplayOptionAccessor<boolean>
  minValue: DisplayOptionAccessor<number | undefined>
  maxValue: DisplayOptionAccessor<number | undefined>
}

/*
 COMPONENTS
*/

export const BrowserFilters: React.FC<FiltersProps> = ({
  categoryTypes,
  categoryId,
  partyId,
  missingParty,
  minValue,
  maxValue,
}) => {
  const summaryFilters = React.useMemo(
    () => ({
      categoryTypes,
    }),
    [categoryTypes]
  )

  const selectedCategories = React.useMemo(
    () => new Set<string>([categoryId.get()].filter(defined)),
    [categoryId]
  )

  const toggleCategory = React.useCallback(
    (category: EntityCategory) => {
      if (category.id === categoryId.get()) {
        categoryId.clear()
        return
      }
      categoryId.set(category.id)
    },
    [categoryId]
  )

  const selectedParties = React.useMemo(
    () => new Set<string>([partyId.get()].filter(defined)),
    [partyId]
  )

  const toggleParty = React.useCallback(
    (party: EntityParty) => {
      if (party.id === partyId.get()) {
        partyId.clear()
        return
      }
      partyId.set(party.id)
    },
    [partyId]
  )

  const toggleIncludeMissingParty = React.useCallback(() => {
    if (missingParty.get()) {
      missingParty.clear()
      return
    }
    missingParty.set(true)
  }, [missingParty])

  const setValue = React.useCallback(
    (accessor: DisplayOptionAccessor<number | undefined>) => (value: number | undefined) => {
      if (value === undefined) {
        accessor.clear()
        return
      }
      accessor.set(value)
    },
    []
  )

  return (
    <>
      <FilterByParty
        selectedParties={selectedParties}
        toggleParty={toggleParty}
        includeMissingParty={missingParty.get() || undefined}
        toggleIncludeMissingParty={toggleIncludeMissingParty}
        filters={summaryFilters}
        skip={false}
      />

      <FilterByCategory
        selectedCategories={selectedCategories}
        toggleCategory={toggleCategory}
        filters={summaryFilters}
        skip={false}
      />

      <FilterByAmount
        minValue={minValue.get()}
        maxValue={maxValue.get()}
        setMinValue={setValue(minValue)}
        setMaxValue={setValue(maxValue)}
        filters={summaryFilters}
      />
    </>
  )
}
